import type { StagedAction } from '../../../hooks/staged-actions/types'

type SendReadyPlatform = 'slack' | 'gmail' | 'google_calendar' | 'linear'

/**
 * Where each platform planner v2 can send on keeps its body and its recipient.
 * The body fields match the gateway's `BODY_FIELD` (planner-v2/stored-draft.ts).
 * A calendar event can be created with no guests and no description, so it
 * requires neither. A ticket needs its team, and files without a description.
 */
interface SendFields {
  toolName: string
  body: string
  bodyRequired: boolean
  recipient: string
  recipientRequired: boolean
}

const SEND_READY_FIELDS: Record<SendReadyPlatform, SendFields> = {
  slack: {
    toolName: 'post_message',
    body: 'markdown_text',
    bodyRequired: true,
    recipient: 'channel_id',
    recipientRequired: true,
  },
  gmail: { toolName: 'send_email', body: 'body', bodyRequired: true, recipient: 'to', recipientRequired: true },
  google_calendar: {
    toolName: 'create_event',
    body: 'description',
    bodyRequired: false,
    recipient: 'attendees',
    recipientRequired: false,
  },
  linear: {
    toolName: 'save_issue',
    body: 'description',
    bodyRequired: false,
    recipient: 'team',
    recipientRequired: true,
  },
}

export type MissingForSend = 'recipient' | 'body'

export interface SendReadiness {
  platform: SendReadyPlatform
  /** The body holds words, whoever wrote them. */
  drafted: boolean
  /** What still has to be filled before the action can go out; empty when it can. */
  missing: Array<MissingForSend>
  /** Empty but worth filling, though the action can go out without it. */
  suggested: Array<MissingForSend>
}

const isFilled = (value: unknown): boolean =>
  typeof value === 'string' ? value.trim().length > 0 : Array.isArray(value) && value.length > 0

const isSendReadyPlatform = (toolSet: string | undefined): toolSet is SendReadyPlatform =>
  toolSet !== undefined && Object.prototype.hasOwnProperty.call(SEND_READY_FIELDS, toolSet)

/**
 * What a send-ready action still needs before it can go out, read from its
 * input. Its view renders an empty Slack body as "No content", which would read
 * as drafted. Undefined for an action that isn't one of the send-ready kinds.
 */
export function sendReadiness(
  action: Pick<StagedAction, 'toolSet' | 'toolName' | 'inputData'>,
): SendReadiness | undefined {
  if (!isSendReadyPlatform(action.toolSet)) {
    return undefined
  }
  const platform = action.toolSet
  const fields = SEND_READY_FIELDS[platform]
  if (fields.toolName !== action.toolName) {
    return undefined
  }
  const input = (action.inputData ?? {}) as Record<string, unknown>
  const drafted = isFilled(input[fields.body])
  const missing: Array<MissingForSend> = []
  const suggested: Array<MissingForSend> = []
  if (!isFilled(input[fields.recipient])) {
    if (fields.recipientRequired) {
      missing.push('recipient')
    } else {
      suggested.push('recipient')
    }
  }
  if (fields.bodyRequired && !drafted) {
    missing.push('body')
  }
  return { platform, drafted, missing, suggested }
}

/** A send-ready action whose body is still blank, an event's description included, so it waits for a draft rather than opening or sending. */
export function isUndrafted(action: Pick<StagedAction, 'toolSet' | 'toolName' | 'inputData'>): boolean {
  return sendReadiness(action)?.drafted === false
}

/** The action's body as it stands, empty when it has none or isn't send-ready. */
export function sendBody(action: Pick<StagedAction, 'toolSet' | 'toolName' | 'inputData'>): string {
  const readiness = sendReadiness(action)
  const body =
    readiness && (action.inputData as Record<string, unknown> | undefined)?.[SEND_READY_FIELDS[readiness.platform].body]
  return typeof body === 'string' ? body.trim() : ''
}

/** The line under the body saying what to fill in, required or not; undefined when there's nothing to add. */
export function sendHint({ platform, missing, suggested }: SendReadiness): string | undefined {
  const recipient = missing.includes('recipient')
  const body = missing.includes('body')
  if (recipient && platform === 'linear') return 'Pick a team to file it'
  if (recipient && body) return 'Pick a recipient and draft the message to send'
  if (recipient) return 'Pick a recipient to send'
  if (body) return 'Draft the message to send'
  if (platform === 'google_calendar' && suggested.includes('recipient')) return 'Add a guest (optional)'
  return undefined
}
